import { useQuery, useQueryClient } from 'react-query';
import { useNavigate } from 'react-router';
import { LoggingServiceAdapter } from 'src/backoffice-app-code/externalServices/adapters/LoggingServiceAdapter';
import { NotificationAdapter } from 'src/backoffice-app-code/externalServices/adapters/NotificationServiceAdapter';
import {
  FETCH_USER,
  IS_LOGGED_IN,
} from 'src/backoffice-app-code/libs/reactQuery/configs/reactQueryCashKeys';
import {
  defaultRQConfig,
  staticDataDefaultRQConfig,
} from 'src/backoffice-app-code/libs/reactQuery/configs/reactQueryConfigs';
import { LoginRoot } from 'src/backoffice-app-code/routes/Routes';
import { getServerErrorMessage } from 'src/backoffice-app-code/utils/errorUtils';
import { userExtension } from '../../infrastracture/DTOs/UserDTO';

import { AuthRepository } from '../../infrastracture/repositories/AuthRepository';
import { getUserHomeRoute, navigateUserToProfile, User } from '../../models/User';
import { clearStoredUser, getStoredUser, setStoredUser } from '../localStorages/userLocalStorage';

interface IUseUser {
  user: User | undefined;
  updateUser: (user: User) => void;
  clearUser: () => void;
  isLoading: boolean;
  errorMessage: string | undefined;
  navigateToProfile: () => void;
  getHomeRoute: () => string;
}

export const useUser = (): IUseUser => {
  const { LogError } = LoggingServiceAdapter;
  const { EnqueueMessage } = NotificationAdapter();
  const queryClient = useQueryClient();
  const isLoggedIn = queryClient.getQueryData<boolean>([IS_LOGGED_IN]);
  const navigate = useNavigate();

  const {
    data: user,
    error,
    isLoading,
  } = useQuery(
    [FETCH_USER, isLoggedIn],
    async () => {
      const result = await AuthRepository.GetUser();
      return result ? userExtension(result) : undefined;
    },
    {
      initialData: getStoredUser(),
      ...defaultRQConfig,
      ...staticDataDefaultRQConfig,
      enabled: !!isLoggedIn,
      onSuccess: (data: undefined | User) => {
        if (!data) {
          clearStoredUser();
        } else {
          setStoredUser(data);
        }
      },
      onError: (error: unknown) => {
        EnqueueMessage(getServerErrorMessage(error), 'error');
        LogError(error, 'Error while calling api', 'useUser->getUser');
        navigate(LoginRoot);
        queryClient.setQueryData([IS_LOGGED_IN], false);
      },
    }
  );

  const navigateToProfile = () => {
    navigateUserToProfile(user, navigate);
  };

  const getHomeRoute = (): string => {
    return getUserHomeRoute(user);
  };

  function updateUser(newUser: User): void {
    queryClient.setQueryData([FETCH_USER], newUser);
    setStoredUser(newUser);
  }

  function clearUser() {
    queryClient.invalidateQueries([FETCH_USER]);
    clearStoredUser();
  }

  return {
    user,
    isLoading,
    errorMessage: error ? getServerErrorMessage(error) : undefined,
    updateUser,
    clearUser,
    navigateToProfile,
    getHomeRoute,
  };
};
